import React, { createContext, useEffect, useState } from 'react'
import propTypes from 'prop-types'

import useFeatureConfig, { FeatureNames } from '@hooks/useFeatureConfig'

export const ActionablesContext = createContext(null)

export const ActionablesProvider = ({ children, ...actionableProps }) => {
  const [contextValue, setContextValue] = useState({})
  const [
    editImplementedPlanConfig,
    editJointScenarioConfig
  ] = useFeatureConfig(
    FeatureNames.SCENARIO__EDIT_IMPLEMENTED_PLAN,
    FeatureNames.JOINT_PLAN__EDIT
  )

  useEffect(
    () => setContextValue(
      constructActionablesObject(
        actionableProps,
        editImplementedPlanConfig,
        editJointScenarioConfig
      )
    ),
    []
  )
  return (
    <ActionablesContext.Provider value={contextValue}>
      {children}
    </ActionablesContext.Provider>
  )
}

const constructActionablesObject = (actionableProps, editImplementedPlanConfig, editJointScenarioConfig) => {
  const { location, planType } = actionableProps

  if (planType === 'historic') return historicPlanActionablesObject(actionableProps);

  switch (location) {
    case 'implementedPlan':
      return implementedPlanActionablesObject(actionableProps, editImplementedPlanConfig)
    case 'scenarioPlanning':
      return scenarioPlanningActionablesObject(actionableProps)
    case 'onboarding':
      return {
        location,
        riskReadOnly: false
      }
    case 'jointScenario':
      return jointScenarioActionablesObject(actionableProps, editJointScenarioConfig)
    case 'metricsDashboard':
    default:
      return {
        readOnly: true,
        quoteReadOnly: true,
        cashflowEventsReadOnly: true,
        implementable: false
      }
  }
}

const implementedPlanActionablesObject = (
  { location, featureToggles, showTalkToAWealthManager, planType, userDetails },
  editImplementedPlanConfig
) => {
  const { historicScenarioCount } = editImplementedPlanConfig.getOptions()
  const { isWealthManager } = userDetails
  const readOnly = !editImplementedPlanConfig.isEnabled() && planType === 'implemented'
  const isWealthManagerWithHistoricPlan = isWealthManager
    && planType === 'implemented'
    && historicScenarioCount > 0

  return {
    readOnly,
    astuteCcp: featureToggles.astuteCcp,
    jointPlanning: featureToggles.jointPlanning,
    showTalkToAWealthManager,
    quoteReadOnly: true,
    cashflowEventsReadOnly: readOnly || isWealthManagerWithHistoricPlan,
    riskReadOnly: readOnly || isWealthManagerWithHistoricPlan,
    implementable: false,
    fnaReadOnly: readOnly,
    location
  }
}

const scenarioPlanningActionablesObject = ({ quotes, retirementScenarioStatus, userDetails, location, featureToggles, showTalkToAWealthManager }) => ({
  readOnly: quotes.some((quote) => quote.status === 'accepted'),
  showTalkToAWealthManager,
  quoteReadOnly: quotes.some((quote) => quote.status === 'accepted') || !userDetails.isWealthManager,
  cashflowEventsReadOnly: quotes.some((quote) => quote.status === 'accepted'),
  implementable: !(IMPLEMENTABLE_SCENARIO_STASUSES.includes(retirementScenarioStatus) && quotes.some((quote) => quote.status === 'created')),
  fnaReadOnly: false,
  location
})

const historicPlanActionablesObject = ({ location }) => {
  return {
    readOnly: true,
    astuteCcp: false,
    jointPlanning: false,
    showTalkToAWealthManager: false,
    quoteReadOnly: true,
    cashflowEventsReadOnly: true,
    riskReadOnly: true,
    implementable: false,
    fnaReadOnly: true,
    location,
    frozen: true
  }
}

const jointScenarioActionablesObject = (_, editJointScenarioConfig) => ({
  readOnly: !editJointScenarioConfig.isEnabled(),
})

const IMPLEMENTABLE_SCENARIO_STASUSES = [
  'draft',
  'in_review'
]

ActionablesProvider.propTypes = {
  location: propTypes.string.isRequired,
  retirementScenarioStatus: propTypes.string,
  userDetails: propTypes.object,
  children: propTypes.node,
  planType: propTypes.string
}
